import React, { useState, useMemo } from "react";
import { PageHero } from "./AboutPage";
import { EDITIONS } from "../data/editions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const MembersPage = () => {
  const [active, setActive] = useState(0);
  const current = EDITIONS[active];
  const displayName = useMemo(() => current.name || `${current.edition} Edition`, [current]);

  return (
    <div>
      <PageHero
        overline="Line History"
        title="Every Edition. Every Brother."
        subtitle="A living archive of every edition of GODSC &mdash; from the 8th to the 40th &mdash; and the men who answered the call."
      />

      <section className="section-pad">
        <div className="max-w-6xl mx-auto px-5">
          {/* Edition selector dropdown */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10" data-testid="edition-selector">
            <div>
              <div className="overline text-[#C9A227] mb-2">Select an Edition</div>
              <div className="text-sm text-[#5C5C5C]">
                {EDITIONS.length} editions on record
              </div>
            </div>
            <div className="w-full sm:w-[420px]">
              <Select
                value={String(active)}
                onValueChange={(v) => setActive(Number(v))}
              >
                <SelectTrigger
                  className="w-full bg-white border border-[#0A1F44] rounded-none h-12 px-4 text-[#0A1F44] focus:ring-2 focus:ring-[#D4AF37]/40 focus:border-[#D4AF37]"
                  data-testid="edition-select-trigger"
                >
                  <SelectValue>
                    <span className="font-serif-display text-lg">
                      {current.edition} Edition
                    </span>
                    {current.name && (
                      <span className="text-xs tracking-[0.18em] uppercase text-[#5C5C5C] ml-3">
                        &middot; {current.name.length > 40 ? current.name.slice(0, 40) + "\u2026" : current.name}
                      </span>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent
                  className="bg-white border border-[#D8D3C4] rounded-none max-h-[420px]"
                  data-testid="edition-select-content"
                >
                  {EDITIONS.map((l, i) => (
                    <SelectItem
                      key={l.edition}
                      value={String(i)}
                      className="rounded-none focus:bg-[#F5F1E6] focus:text-[#0A1F44] cursor-pointer"
                      data-testid={`edition-option-${i}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-serif-display text-base text-[#0A1F44] min-w-[70px]">
                          {l.edition}
                        </span>
                        <span className="text-xs tracking-[0.16em] uppercase text-[#5C5C5C]">
                          {l.name ? l.name : "\u2014"}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Detail panel - now full width */}
          <div className="bg-[#F5F1E6] border border-[#D8D3C4] p-8 md:p-12" data-testid="line-detail">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <div className="overline text-[#C9A227]">{current.edition} Edition</div>
                <h2 className="font-serif-display text-3xl md:text-4xl text-[#0A1F44] mt-2 leading-tight max-w-3xl">
                  {displayName}
                </h2>
              </div>
              {current.members.length > 0 && (
                <div className="text-right shrink-0">
                  <div className="overline text-[#5C5C5C]">Roster</div>
                  <div className="font-serif-display text-4xl text-[#0A1F44]">
                    {current.members.length}
                  </div>
                </div>
              )}
            </div>

            {current.name && <div className="mt-3 h-[2px] w-16 bg-[#D4AF37]" />}

            <div className="mt-8">
              {current.members.length === 0 ? (
                <div
                  className="bg-white border border-[#D8D3C4] p-6 text-[#5C5C5C] text-sm italic"
                  data-testid="roster-empty"
                >
                  Roster to be added. If you are a brother of this edition and would
                  like your name included, please{" "}
                  <a
                    href="/contact"
                    className="text-[#0A1F44] underline hover:text-[#C9A227]"
                  >
                    contact us
                  </a>
                  .
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {current.members.map((name, i) => (
                    <div
                      key={i}
                      className="bg-white border border-[#D8D3C4] px-4 py-3 text-center"
                      data-testid={`member-${i}`}
                    >
                      <div className="text-[10px] tracking-[0.28em] uppercase text-[#C9A227]">
                        #{i + 1}
                      </div>
                      <div className="font-serif-display text-[18px] text-[#0A1F44] leading-tight mt-1">
                        {name}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MembersPage;
